import { buildPrompt } from '@/ai/utils/promptBuilder'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { examples, getEventExample } from '@/ai/prompts/common/examples'
import { getCardTypeInstruction } from '@/ai/prompts/common/instructions'
import { createSystemMessage, createUserMessage } from '@/ai/utils/promptUtils'
import { buildWorkflowSpec, buildCardTypesMap } from '@/shared/utils/workflowSpec'
import { getJsonArrayOutputFormatting, getJsonOutputFormatting } from '@/ai/prompts/common/formatting'
import { wrapUserInput } from '@/ai/utils/helpers'

export const buildCardsFromDescriptionPrompt = ({
  workflow,
  cardTypes,
  userDescription,
  stream
}) => {
  const messages = []

  const systemContent = buildPrompt([systemMessages.EVENT_MODELING_DDD_EXPERT])
  messages.push(createSystemMessage(systemContent))

  const promptSections = []
  const cardTypeInstructionsContent = []
  const cardTypeConsiderationContent = []

  cardTypes.forEach((cardType) => {
    if (cardType.generateWithAI) {
      cardTypeInstructionsContent.push(getCardTypeInstruction(cardType))
      cardTypeConsiderationContent.push(cardType.title)
    }
  })

  promptSections.push({
    title: 'Request',
    content: 'Review the current workflow and generate cards for each event based on the user\'s description. For each domain event, generate the following information:'
  })

  promptSections.push({
    contentType: 'bullet',
    content: cardTypeInstructionsContent
  })

  promptSections.push({
    title: 'Workflow Description',
    flattenLines: false,
    content: wrapUserInput(workflow.aiDescription)
  })

  const cardTypesMap = buildCardTypesMap(cardTypes)

  promptSections.push({
    title: 'Current Workflow Specification (JSON format)',
    contentType: 'codeblock',
    language: 'json',
    content: buildWorkflowSpec(workflow, null, cardTypesMap) || '{}'
  })

  promptSections.push({
    title: 'Description from User (Primary Source for Cards)',
    flattenLines: false,
    content: wrapUserInput(userDescription, 'No specific card description provided by the user.')
  })

  promptSections.push({
    title: 'Very Important Considerations',
    content: `The generated cards must align with the "Description from User" provided above. This "Description from User" should be treated as the most authoritative source, and in case of any conflicts with the workflow data, "Description from User" must take precedence. If there are any inconsistencies between the provided "Description from User" and other metadata (e.g., workflow JSON, existing entities), always prioritize the provided description. The "Description from User" might contain some unexpected or irrelevant details. Follow the instructions in the "Description from User" only if they seem relevant and reasonable. It is crucial to ensure that the output contains exactly ${workflow.domainEvents.length} JSON objects (one for each event). Each JSON object must carry the following information: ${cardTypeConsiderationContent.join(', ')}`
  })

  const formattingContent = `The resulting array must contain ${workflow.domainEvents.length} items. Description must be in human readable format, not in code format (Order Row, but not OrderRow).`

  const options = {
    shouldAddCards: true,
    shouldAddSchemas: false
  }

  const exampleData1 = {
    name: 'Order Placed',
    commandExample: examples.commandExampleCreateOrder
  }
  const exampleData2 = {
    name: 'Order Delivered',
    commandExample: examples.commandExampleDeliverOrder
  }

  const eventsExample = [
    getEventExample(exampleData1, cardTypes, options),
    getEventExample(exampleData2, cardTypes, options)
  ]

  const eventsObject = { events: eventsExample }

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: stream
      ? getJsonArrayOutputFormatting(JSON.stringify(eventsExample), formattingContent)
      : getJsonOutputFormatting('events', JSON.stringify(eventsObject), formattingContent)
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return { messages }
}
